import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";
import { base64ADatos } from "./assessmentExtractor";
import { obtenerDimensionesImagen, ajustarProporcional } from "./imageDimensions";

const RUTA_MACHOTE = `${process.env.PUBLIC_URL || ""}/documentos/Machote_Kickoff.pptx`;

const RUTAS_LOGO_EMPRESA = {
  Netjer: `${process.env.PUBLIC_URL || ""}/img/NJ-Logotipo.png`,
  Rexten: `${process.env.PUBLIC_URL || ""}/img/rexten.png`,
};

const NOMBRES_EMPRESA = {
  Netjer: "Netjer Networks México",
  Rexten: "Rexten",
};

const PIXEL_TRANSPARENTE_BASE64 = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";

const cargarLogoEmpresa = async (empresa) => {
  const ruta = RUTAS_LOGO_EMPRESA[empresa] || RUTAS_LOGO_EMPRESA.Netjer;
  const respuesta = await fetch(ruta);
  if (!respuesta.ok) {
    throw new Error(`No se pudo cargar el logo de la empresa (${ruta}).`);
  }
  return new Uint8Array(await respuesta.arrayBuffer());
};

const TAMANO_MAXIMO_LOGO_PORTADA = [260, 130];
const TAMANO_MAXIMO_LOGO_CLIENTE_PORTADA = [160, 65];
const TAMANO_MAXIMO_LOGO_EMPRESA_ENCABEZADO = [95, 34];
const TAMANO_MAXIMO_LOGO_CLIENTE_ENCABEZADO = [110, 40];

const TAMANOS_MAXIMOS_POR_TAG = {
  logoEmpresaPortada: TAMANO_MAXIMO_LOGO_PORTADA,
  logoCliente: TAMANO_MAXIMO_LOGO_CLIENTE_PORTADA,
  logoEmpresaEncabezado: TAMANO_MAXIMO_LOGO_EMPRESA_ENCABEZADO,
  logoClienteEncabezado: TAMANO_MAXIMO_LOGO_CLIENTE_ENCABEZADO,
};

const crearModuloImagenes = () =>
  new ImageModule({
    centered: false,
    fileType: "pptx",
    getImage: (tagValue) => tagValue,
    getSize: (imgBuffer, _tagValue, tagName) => {
      const tamanoMaximo = TAMANOS_MAXIMOS_POR_TAG[tagName] || TAMANO_MAXIMO_LOGO_CLIENTE_PORTADA;
      return ajustarProporcional(obtenerDimensionesImagen(imgBuffer), tamanoMaximo);
    },
  });

const marcasCronogramaCompletas = (marcas) => {
  const completas = {};
  for (let f = 1; f <= 7; f += 1) {
    for (let s = 1; s <= 7; s += 1) {
      const clave = `cronP${f}S${s}`;
      completas[clave] = (marcas && marcas[clave]) || "";
    }
  }
  return completas;
};

const eliminarDiapositivas = (zip, numerosDiapositiva) => {
  if (!numerosDiapositiva || numerosDiapositiva.length === 0) return;

  const rutaPresentacion = "ppt/presentation.xml";
  const rutaRelsPresentacion = "ppt/_rels/presentation.xml.rels";
  const rutaContentTypes = "[Content_Types].xml";

  let presentacionXml = zip.file(rutaPresentacion).asText();
  let relsXml = zip.file(rutaRelsPresentacion).asText();
  let contentTypesXml = zip.file(rutaContentTypes).asText();

  numerosDiapositiva.forEach((numero) => {
    const objetivoTarget = `slides/slide${numero}.xml`;
    const coincidenciaRel = relsXml.match(
      new RegExp(`<Relationship [^>]*Target="${objetivoTarget}"[^>]*/>`)
    );
    if (!coincidenciaRel) return;
    const rId = coincidenciaRel[0].match(/Id="(rId\d+)"/)[1];

    presentacionXml = presentacionXml.replace(
      new RegExp(`<p:sldId[^>]*r:id="${rId}"[^>]*/>`),
      ""
    );
    relsXml = relsXml.replace(coincidenciaRel[0], "");
    contentTypesXml = contentTypesXml.replace(
      new RegExp(`<Override[^>]*PartName="/ppt/slides/slide${numero}\\.xml"[^>]*/>`),
      ""
    );

    zip.remove(`ppt/slides/slide${numero}.xml`);
    zip.remove(`ppt/slides/_rels/slide${numero}.xml.rels`);
  });

  zip.file(rutaPresentacion, presentacionXml);
  zip.file(rutaRelsPresentacion, relsXml);
  zip.file(rutaContentTypes, contentTypesXml);
};

const DIAPOSITIVAS_POR_SECCION = {
  tieneObjetivo: [2],
  tieneBom: [3],
  tieneCronograma: [4, 6],
};

export const generarDocumentoKickoff = async ({
  nombreProyecto,
  bomIntro,
  logoCliente,
  empresa,
  objetivoItems,
  bomFilas,
  marcasCronograma,
  tieneObjetivo = true,
  tieneBom = true,
  tieneCronograma = true,
}) => {
  const [respuesta, datosLogoEmpresa] = await Promise.all([
    fetch(RUTA_MACHOTE),
    cargarLogoEmpresa(empresa),
  ]);
  if (!respuesta.ok) {
    throw new Error("No se pudo cargar el machote de Kick-off (public/documentos/Machote_Kickoff.pptx).");
  }
  const arrayBuffer = await respuesta.arrayBuffer();
  const zip = new PizZip(arrayBuffer);

  const doc = new Docxtemplater(zip, {
    paragraphLoop: true,
    linebreaks: true,
    nullGetter: () => "",
    modules: [crearModuloImagenes()],
  });

  const datosLogoCliente = logoCliente
    ? base64ADatos(logoCliente.base64)
    : base64ADatos(PIXEL_TRANSPARENTE_BASE64);

  try {
    await doc.renderAsync({
      nombreProyecto,
      bomIntro: bomIntro || "",
      nombreEmpresa: NOMBRES_EMPRESA[empresa] || empresa,
      logoEmpresaPortada: datosLogoEmpresa,
      logoEmpresaEncabezado: datosLogoEmpresa,
      logoCliente: datosLogoCliente,
      logoClienteEncabezado: datosLogoCliente,
      objetivoItems: objetivoItems && objetivoItems.length > 0 ? objetivoItems : [{ texto: "" }],
      bomFilas: bomFilas && bomFilas.length > 0 ? bomFilas : [{ col1: "", col2: "", col3: "" }],
      ...marcasCronogramaCompletas(marcasCronograma),
    });
  } catch (error) {
    const detalle = error.properties && error.properties.errors
      ? error.properties.errors.map((e) => e.message).join("; ")
      : error.message;
    throw new Error(`No se pudo generar el Kick-off a partir del machote: ${detalle}`);
  }

  const seccionesPresentes = { tieneObjetivo, tieneBom, tieneCronograma };
  const numerosAEliminar = Object.entries(DIAPOSITIVAS_POR_SECCION)
    .filter(([clave]) => !seccionesPresentes[clave])
    .flatMap(([, numeros]) => numeros);
  eliminarDiapositivas(doc.getZip(), numerosAEliminar);

  return doc.toBlob();
};